import { MODE_OPTIONS } from '@/components/theme-toggle.component';
import { SIGN_IN_PATH } from '@/constants/pages';
import { useLanguage } from '@/hooks/use-language.hook';
import { useThemeMode, type ThemeMode } from '@/hooks/use-theme-mode.hook';
import type { SupportedLocale } from '@/lib/i18n/config';
import { Check, Menu as MenuIcon } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  type SxProps,
  type Theme
} from '@mui/material';
import Link from 'next/link';
import { useState, type FunctionComponent, type MouseEvent } from 'react';

type NavLink = {
  label: string;
  href: string;
};

type HeaderMobileMenuProps = {
  sx?: SxProps<Theme>;
  signInLabel: string;
  navLinks: ReadonlyArray<NavLink>;
};

/**
 * Mobile-only hamburger menu for the landing header. Collapses everything
 * secondary — Sign in, the nav links, and the language/theme controls — into a
 * single dropdown so the mobile bar stays uncluttered (Logo + Get started + ☰).
 * Language/theme options are sourced from the same hooks/data as the standalone
 * `LanguageSwitcher` and `ThemeToggle` so the lists stay single-source.
 */
export const HeaderMobileMenu: FunctionComponent<HeaderMobileMenuProps> = ({
  sx,
  signInLabel,
  navLinks
}) => {
  const { language, setLanguage, available } = useLanguage();
  const { mode, setMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLanguage = (locale: SupportedLocale) => {
    setLanguage(locale);
    handleClose();
  };

  const handleTheme = (value: ThemeMode) => {
    setMode(value);
    handleClose();
  };

  return (
    <>
      <IconButton
        id="header-mobile-menu-button"
        aria-label="Open menu"
        aria-haspopup="menu"
        aria-controls={open ? 'header-mobile-menu' : undefined}
        aria-expanded={open || undefined}
        onClick={handleOpen}
        sx={[{ color: 'text.secondary' }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <MenuIcon fontSize="small" />
      </IconButton>
      <Menu
        id="header-mobile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: { sx: { minWidth: 200 } },
          list: { 'aria-labelledby': 'header-mobile-menu-button' }
        }}
      >
        <MenuItem component={Link} href={SIGN_IN_PATH} onClick={handleClose}>
          <ListItemText>{signInLabel}</ListItemText>
        </MenuItem>
        {navLinks.map(({ label, href }) => (
          <MenuItem key={href} component="a" href={href} onClick={handleClose}>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <ListSubheader disableSticky>Language</ListSubheader>
        {available.map(({ code, label }) => {
          const selected = code === language;
          return (
            <MenuItem
              key={code}
              selected={selected}
              onClick={() => handleLanguage(code)}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {selected ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
        <ListSubheader disableSticky>Theme</ListSubheader>
        {MODE_OPTIONS.map(({ value, label, Icon }) => {
          const selected = mode === value;
          return (
            <MenuItem
              key={value}
              selected={selected}
              onClick={() => handleTheme(value)}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
              {selected ? <Check fontSize="small" /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
